import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { CircleMinus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { BoardPhase } from "../dtos";
import { PHASE_STATUS_LABEL } from "../utils/derive-board";
import StepStatusIcon from "./step-status-icon";

/** Design asset "fill icon/error-filled": the folder tab's rejected glyph is a SOLID 24px disc
 * with a white cross, not lucide's outline circle-x (which reads far lighter at tab size). */
const CrossFilled = ({ size, fill }: { size: number; fill?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="12" fill={fill} />
    <path
      d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * §PhaseSignals: the tab reports EVERY state the phase carries, not just the one word the
 * uiStatus ladder collapses to. A phase can be Done and still owe a re-upload, or be Current and
 * hold a reopened step — before this, the ladder's first rung ("every step settled ⇒ DONE") hid
 * both behind a clean "Done" and a single anonymous orange dot that never said what it meant.
 * Drawn on the Indicator ramp at the badge metrics the step rows already use.
 */
const SIGNAL = [
  { key: "rejected", label: "Rejected", bg: "#FFC8C2", fg: "#AB2018" },
  { key: "editsNeeded", label: "Edits needed", bg: "#FFC8C2", fg: "#AB2018" },
  { key: "conditional", label: "Conditional", bg: "#FFE3CC", fg: "#B34B00" },
  { key: "reopened", label: "Reopened", bg: "#E0E0E0", fg: "#313131" },
  // §MD-bypass: a Managing Director bypassed this phase — amber, the same warning ramp as
  // "Conditional". Renders regardless of `uiStatus` like every other signal here: `phaseSignals`
  // computes it straight from `phase.bypass`, independent of what any step underneath reads.
  { key: "bypassed", label: "Bypassed by MD", bg: "#FFE3CC", fg: "#B34B00" },
] as const;

/**
 * Browser-tab style phase chips per the Figma ("1 Onboarding — Current | 2 Finance —
 * Upcoming …"): the active tab carries a top/left/right border and its white background
 * hides the content container's top border underneath (the stack overlaps it by 1px).
 * A DONE tab is entirely Text/Positive (glyph, title AND caption) and a BLOCKER tab entirely
 * Text/Negative — that colour outranks the selected/accent treatment, per the Figma.
 */
const PhaseStepper = ({
  phases,
  selected,
  onSelect,
}: {
  phases: BoardPhase[];
  selected: string | null;
  onSelect: (phaseName: string) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;

  /**
   * §ScrollAffordance: state for the overlay scrollbar. `widthPct` is the visible fraction of the
   * strip and `leftPct` how far along it is — the same two numbers a native thumb encodes.
   */
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState({ visible: false, widthPct: 100, leftPct: 0 });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    // 2px of slack: sub-pixel widths otherwise show a thumb on a strip that cannot actually move
    if (scrollWidth <= clientWidth + 2) {
      setThumb((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    setThumb({
      visible: true,
      widthPct: Math.max((clientWidth / scrollWidth) * 100, 12),
      leftPct: (scrollLeft / scrollWidth) * 100,
    });
  }, []);

  // Re-measure whenever the strip can change width: phases arriving, signal chips appearing (they
  // widen a tab), the window resizing, or the lead page's own panels reflowing around it.
  useEffect(() => {
    measure();
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, phases.length, selected]);

  /** Drag the thumb: translate pointer movement into scrollLeft through the same ratio. */
  const onThumbDown = useCallback((event: React.MouseEvent) => {
    const el = scrollerRef.current;
    if (!el) return;
    event.preventDefault();
    const startX = event.clientX;
    const startLeft = el.scrollLeft;
    const ratio = el.scrollWidth / el.clientWidth;
    const onMove = (move: MouseEvent) => {
      el.scrollLeft = startLeft + (move.clientX - startX) * ratio;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  // design rejection red is red[700] (#C72D22), same as text.extension.negative
  const negative = colors.semantic.text.extension.negative;
  // design "Text/Positive" is green[700] (#3E8D7B) — the tab was using the lighter green[500]
  const positive = colors.semantic.text.extension.positive;

  const captionColor = (phase: BoardPhase, isSelected: boolean): string | undefined => {
    if (phase.uiStatus === "DONE") return positive;
    if (phase.uiStatus === "BLOCKER") return negative;
    return isSelected
      ? colors.semantic.text.accent
      : colors.semantic.text.tertiary;
  };

  const renderIndicator = (phase: BoardPhase, isSelected: boolean) => {
    // the design's tab glyph slot is 24px in every state (same box as the number pill below)
    if (phase.uiStatus === "DONE") {
      // reuse the delivered "success-filled" asset — the tab wants the same solid disc the
      // step cards wear, not an outline check
      return <StepStatusIcon status="DONE" size={24} />;
    }
    if (phase.uiStatus === "BLOCKER") {
      return <CrossFilled size={24} fill={negative} />;
    }
    if (phase.uiStatus === "SKIPPED") {
      // whole phase auto-skipped — grey minus, not the green "work done" check
      return <CircleMinus size={24} color={colors.semantic.text.tertiary} />;
    }
    return (
      <Box
        sx={{
          minWidth: 24,
          height: 24,
          px: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: theme.nawy.radius.full,
          fontSize: 14,
          lineHeight: "20px",
          backgroundColor: isSelected
            ? colors.semantic.layer.accentLight
            : colors.semantic.layer.secondary,
          color: isSelected
            ? colors.semantic.text.accent
            : colors.semantic.text.primary,
        }}
      >
        {phase.order}
      </Box>
    );
  };

  return (
    // §FolderSeam + §ScrollAffordance: the strip needs a visible scroll handle AND its selected tab
    // has to meet the container's border. A native horizontal scrollbar cannot do both — it is laid
    // out INSIDE the scroller, so its 11px pushed the container down and left a 10px dead gap at the
    // seam. So the native bar stays hidden and this wrapper draws an OVERLAY one: same place, same
    // job, but positioned out of flow so it costs no height. It rides in the tabs' own 12px bottom
    // padding, ABOVE the seam line, so it never crosses the border it used to break.
    <Box sx={{ position: "relative", minWidth: 0, maxWidth: "100%", mb: "-1px" }}>
    <Stack
      direction="row"
      ref={scrollerRef}
      onScroll={measure}
      sx={{
        overflowX: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
        // §Overflow (Safari): the scroller must never report its content width as its own minimum —
        // 11 tabs × 128px would otherwise widen every flex ancestor that forgot `min-width: 0`.
        minWidth: 0,
        maxWidth: "100%",
        alignItems: "stretch",
        position: "relative",
        zIndex: 1,
      }}
    >
      {phases.map((phase) => {
        const isSelected = phase.name === selected;
        // a phase can carry several at once (Done + Conditional, Current + Reopened, …)
        const activeSignals = SIGNAL.filter((signal) => phase.signals?.[signal.key]);
        return (
          <ButtonBase
            key={phase.name}
            onClick={() => onSelect(phase.name)}
            sx={{
              minWidth: 128,
              px: 2,
              py: 1.5,
              flexShrink: 0,
              justifyContent: "flex-start",
              // §PhaseSignals alignment: the strip stretches every tab to the tallest one, and a tab
              // carrying signal chips ("Conditional", "Reopened") is two rows taller than its
              // neighbours. ButtonBase centres its content by default, so those neighbours sank to
              // the middle of the stretched row while the tall tab's title stayed at the top — the
              // titles stopped sharing a baseline the moment any chip appeared. Pin content to the
              // top so every tab reads off the same line whatever it carries.
              alignItems: "flex-start",
              // design 12107:50018: the selected tab's stroke sits INSIDE its 60px box. A CSS
              // border adds a 61st pixel (height here is content-driven, so border-box can't
              // absorb it) and lifts every tab a pixel off the container edge — so the
              // left/top/right rule is drawn as an inset shadow, which also retires the
              // transparent-border height filler the inactive tabs used to need.
              border: "none",
              boxShadow: isSelected
                ? [
                    `inset 1px 0 0 ${colors.semantic.border.secondary}`,
                    `inset -1px 0 0 ${colors.semantic.border.secondary}`,
                    `inset 0 1px 0 ${colors.semantic.border.secondary}`,
                  ].join(", ")
                : "none",
              borderTopLeftRadius: theme.nawy.radius.xl,
              borderTopRightRadius: theme.nawy.radius.xl,
              backgroundColor: isSelected
                ? colors.semantic.layer.primary
                : "transparent",
            }}
          >
            {/* design 12107:50018: the tab is `items-start` — the number pill sits on the title's
                line, not centred against the two-line block */}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {renderIndicator(phase, isSelected)}
              {/* design: title→caption gap is xxxs (2px); without it the tab measured 59px against
                  the design's 60px, so every tab sat a pixel short of the container edge */}
              <Box textAlign="left" sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: "18px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    // design: a rejected phase turns its tab title red, and a done phase turns
                    // it green — both outrank the selected/accent treatment (in the Figma the
                    // SELECTED "Documents — Done" tab is still fully Text/Positive)
                    color:
                      phase.uiStatus === "BLOCKER"
                        ? negative
                        : phase.uiStatus === "DONE"
                          ? positive
                          : isSelected
                            ? colors.semantic.text.accent
                            : colors.semantic.text.primary,
                  }}
                >
                  {phase.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    lineHeight: "16px",
                    color: captionColor(phase, isSelected),
                  }}
                >
                  {phase.badge?.label ?? PHASE_STATUS_LABEL[phase.uiStatus]}
                </Typography>
                {/* every OTHER state this phase carries — these survive phase switching, so a
                    rejected document or an unread condition two tabs away still announces itself,
                    and by name rather than as an unexplained dot */}
                {activeSignals.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}
                  >
                    {activeSignals.map((signal) => (
                      <Box
                        key={signal.key}
                        sx={{
                          px: "4px",
                          py: 0,
                          borderRadius: theme.nawy.radius.md,
                          backgroundColor: signal.bg,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            lineHeight: "16px",
                            fontWeight: 400,
                            color: signal.fg,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {signal.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </ButtonBase>
        );
      })}
    </Stack>
      {/* the overlay bar itself — only while there is somewhere to scroll. `pointer-events: none` on
          the track keeps tab clicks working; the thumb takes them back so it can be dragged. */}
      {thumb.visible && (
        <Box
          sx={{
            position: "absolute",
            left: 8,
            right: 8,
            bottom: 4,
            height: 6,
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            onMouseDown={onThumbDown}
            sx={{
              position: "absolute",
              top: 0,
              height: 6,
              borderRadius: 3,
              width: `${thumb.widthPct}%`,
              left: `${thumb.leftPct}%`,
              backgroundColor: colors.semantic.border.primaryDark,
              opacity: 0.55,
              cursor: "grab",
              pointerEvents: "auto",
              transition: "opacity 120ms",
              "&:hover": { opacity: 0.85 },
              "&:active": { cursor: "grabbing", opacity: 0.95 },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default PhaseStepper;
